import { Fragment, useEffect, useRef, useState, useSyncExternalStore } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SiteFilterSelect } from "../operations/shared/SiteFilterSelect";
import { NoAccess } from "../equipment/shared/NoAccess";
import { VerifyPanel } from "../equipment/DailyLogTab";
import { BackfillDialog, formatDateDDMMYY } from "./BackfillDialog";
import { Search } from "lucide-react";
import { MachineTypeFilter, useMachineTypeFilter } from "@/components/MachineTypeFilter";
import { fetchRef, getRef, getRefVersion, subscribeRef } from "../paymentDesk/shared/refDataStore";

const ALLOWED_ROLES = ["Director", "Manager", "Back Office"];
const SHIFTS = ["Day", "Night"];
const WINDOW_DAYS = 5;

function todayIST() {
  const nowIST = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return nowIST.toISOString().slice(0, 10);
}

function windowStart(dateKey) {
  const d = new Date(dateKey + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - (WINDOW_DAYS - 1));
  return d.toISOString().slice(0, 10);
}

function CellValue({ cell }) {
  if (!cell.verified && cell.hours_run_out_of_range) {
    return (
      <span className="text-sm font-semibold text-red-600" title="Flagged — Hours Run out of range" data-testid="cell-flagged">
        Flagged
      </span>
    );
  }
  if (!cell.verified) {
    return (
      <span className="text-sm font-medium text-red-600" data-testid="cell-unverified">
        Unverified
      </span>
    );
  }
  if (cell.open_shift) {
    return (
      <span className="text-xs text-slate-500" data-testid="cell-open-shift">
        Open Shift
      </span>
    );
  }
  if (cell.hours_run != null) {
    const colorClass = cell.hours_run < 2 ? "text-red-600" : cell.hours_run > 9 ? "text-emerald-600" : "text-slate-900";
    return (
      <span className={`text-sm font-semibold font-mono ${colorClass}`} data-testid="cell-hours-run">
        {cell.hours_run}
      </span>
    );
  }
  if (cell.hours_uncomputable) {
    return (
      <span
        className="text-xs font-medium text-amber-600"
        title="Hours can't be computed from the stored readings — open this cell to correct the reading"
        data-testid="cell-hours-uncomputable"
      >
        Check Reading
      </span>
    );
  }
  return <span className="text-sm text-slate-400">—</span>;
}

function GridCell({ cell, canBackfill, onOpenEntry, onBackfill, testId }) {
  if (!cell || cell.status === "missing") {
    if (!canBackfill) return <div className="min-h-[2rem]" data-testid={testId} />;
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onBackfill}
        title="No entry — click to add one"
        className="block w-full min-h-[2rem] rounded cursor-pointer hover:bg-amber-100 transition-colors"
      />
    );
  }
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onOpenEntry(cell.entry_id)}
      className="block w-full text-left rounded px-1 py-0.5 cursor-pointer hover:bg-slate-100 transition-colors"
    >
      <CellValue cell={cell} />
      {cell.operator_remarks && (
        <span
          className="block text-[11px] leading-tight text-slate-400 truncate max-w-[120px]"
          title={cell.operator_remarks}
          data-testid={`${testId}-remarks`}
        >
          {cell.operator_remarks}
        </span>
      )}
    </button>
  );
}

export default function DailyLogCheckPage() {
  const { user } = useAuth();
  useSyncExternalStore(subscribeRef, getRefVersion);
  const siteOptions = getRef("sites").data;
  const sites = siteOptions.map((s) => ({ code: s.site_id, display_label: s.display_label }));
  const firstLoad = useRef(true);
  const [siteFilter, setSiteFilter] = useState("");
  const [search, setSearch] = useState("");
  const [machineType, setMachineType] = useMachineTypeFilter();
  const [date, setDate] = useState(todayIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editEntry, setEditEntry] = useState(null);
  const [backfill, setBackfill] = useState(null);

  const canBackfill = ALLOWED_ROLES.includes(user?.role);

  useEffect(() => {
    fetchRef("sites");
  }, []);

  const fetchGrid = () => {
    const params = { date_from: windowStart(date), date_to: date };
    if (siteFilter) params.site_id = siteFilter;
    if (search.trim()) params.asset_search = search.trim();
    if (machineType) params.machine_type = machineType;
    return client
      .get("/dashboard/daily-log-check", { params })
      .then((res) => setData(res.data))
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  useEffect(() => {
    setLoading(true);
    const delay = firstLoad.current ? 0 : 300;
    firstLoad.current = false;
    const timer = setTimeout(() => {
      fetchGrid().finally(() => setLoading(false));
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteFilter, search, machineType, date]);

  const openEntry = async (entryId) => {
    try {
      const res = await client.get(`/fleetcare/daily-log/${entryId}`);
      setEditEntry(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!ALLOWED_ROLES.includes(user?.role)) return <NoAccess />;

  const assets = data?.assets || [];
  const dates = data?.dates || [];
  const missingCount = assets.reduce(
    (sum, a) =>
      sum + dates.reduce((s, d) => s + SHIFTS.filter((sh) => (a.cells[d]?.[sh]?.status || "missing") === "missing").length, 0),
    0
  );

  return (
    <div className="space-y-6" data-testid="daily-log-check-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Daily Log Machines Check
        </h1>
        <p className="text-sm text-slate-500">5-day Hours Run grid across all active equipment — click a cell to edit, click a blank to backfill.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Site</p>
          <SiteFilterSelect sites={sites} value={siteFilter} onChange={setSiteFilter} testId="daily-log-check-site-filter" placeholder="All sites" allowAll />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Machine type</p>
          <MachineTypeFilter value={machineType} onChange={setMachineType} testId="daily-log-check-machine-type-filter" />
        </div>
        <div className="relative">
          <p className="text-xs font-medium text-slate-500 mb-1">Search machine</p>
          <Search className="absolute left-2.5 top-[34px] h-4 w-4 text-slate-400" />
          <Input
            data-testid="daily-log-check-search-input"
            placeholder="Asset ID, model, type or brand..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-56"
          />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Date</p>
          <Input type="date" data-testid="daily-log-check-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
        </div>
        <Button variant="outline" onClick={() => setDate(todayIST())} data-testid="daily-log-check-today-button">
          Today
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs uppercase text-slate-400 font-mono">Machines</p>
          <p className="text-xl font-bold text-slate-900" data-testid="daily-log-check-total-machines">
            {assets.length}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs uppercase text-slate-400 font-mono">Missing Entries</p>
          <p className="text-xl font-bold text-red-600" data-testid="daily-log-check-total-missing">
            {missingCount}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-auto max-h-[70vh]" data-testid="daily-log-check-grid-scroll">
        <table className="w-full text-sm border-collapse" data-testid="daily-log-check-table">
          <thead>
            <tr>
              <th rowSpan={2} className="sticky top-0 z-20 bg-white border border-slate-200 h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                Machine
              </th>
              <th rowSpan={2} className="sticky top-0 z-20 bg-white border border-slate-200 h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                Site
              </th>
              <th rowSpan={2} className="sticky top-0 z-20 bg-white border border-slate-200 h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                Shift
              </th>
              <th
                colSpan={WINDOW_DAYS}
                className="sticky top-0 z-20 bg-white border border-slate-200 text-center h-9 py-1 font-medium text-muted-foreground"
                data-testid="daily-log-check-hours-run-header"
              >
                Hours Run
              </th>
            </tr>
            <tr>
              {dates.map((d) => (
                <th
                  key={d}
                  className="sticky top-9 z-20 bg-white border border-slate-200 text-center font-mono text-xs py-1 font-medium text-muted-foreground"
                  data-testid={`daily-log-check-date-col-${d}`}
                >
                  {formatDateDDMMYY(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3 + WINDOW_DAYS} className="border border-slate-200 text-center py-8 text-slate-400">
                  Loading...
                </td>
              </tr>
            ) : assets.length === 0 ? (
              <tr>
                <td colSpan={3 + WINDOW_DAYS} className="border border-slate-200 text-center py-8 text-slate-400" data-testid="daily-log-check-empty">
                  No active machines found for this filter.
                </td>
              </tr>
            ) : (
              assets.map((a) => (
                <Fragment key={a.asset_id}>
                  <tr data-testid={`daily-log-check-row-${a.asset_id}-day`}>
                    <td rowSpan={2} className="border border-slate-200 p-2 font-medium align-middle">
                      {a.asset_label}
                    </td>
                    <td rowSpan={2} className="border border-slate-200 p-2 text-sm text-slate-500 align-middle">
                      {a.site_label || "—"}
                    </td>
                    <td className="border border-slate-200 p-2 text-xs text-slate-500 w-14">Day</td>
                    {dates.map((d) => {
                      const isMissing = !a.cells[d]?.Day || a.cells[d]?.Day.status === "missing";
                      return (
                        <td key={d} className={`border border-slate-200 p-1 text-center ${isMissing ? "bg-red-50" : ""}`}>
                          <GridCell
                            cell={a.cells[d]?.Day}
                            canBackfill={canBackfill}
                            onOpenEntry={openEntry}
                            onBackfill={() => setBackfill({ asset: a, dateKey: d, shift: "Day" })}
                            testId={`daily-log-check-cell-${a.asset_id}-day-${d}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                  <tr data-testid={`daily-log-check-row-${a.asset_id}-night`}>
                    <td className="border border-slate-200 p-2 text-xs text-slate-500 w-14">Night</td>
                    {dates.map((d) => {
                      const isMissing = !a.cells[d]?.Night || a.cells[d]?.Night.status === "missing";
                      return (
                        <td key={d} className={`border border-slate-200 p-1 text-center ${isMissing ? "bg-red-50" : ""}`}>
                          <GridCell
                            cell={a.cells[d]?.Night}
                            canBackfill={canBackfill}
                            onOpenEntry={openEntry}
                            onBackfill={() => setBackfill({ asset: a, dateKey: d, shift: "Night" })}
                            testId={`daily-log-check-cell-${a.asset_id}-night-${d}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editEntry && (
        <VerifyPanel
          entry={editEntry}
          canDelete
          canEditRemarks
          onClose={() => setEditEntry(null)}
          onUpdated={(updated) => {
            fetchGrid();
            if (!updated) {
              setEditEntry(null);
              return;
            }
            setEditEntry(updated);
          }}
        />
      )}
      {backfill && (
        <BackfillDialog
          asset={backfill.asset}
          dateKey={backfill.dateKey}
          shift={backfill.shift}
          onClose={() => setBackfill(null)}
          onSaved={() => {
            setBackfill(null);
            fetchGrid();
          }}
        />
      )}
    </div>
  );
}
